<template>
  <div class="dashboard" data-ready="false" ref="dashboardRef">
    <div class="header">
      <h2>Performance Trends</h2>
    </div>

    <!-- Range -->
    <div class="range-selector">
      <button
        v-for="range in ranges"
        :key="range.value"
        :class="{ active: selectedRange === range.value }"
        :disabled="loading"
        @click="changeRange(range.value)"
      >
        {{ range.label }}
      </button>
    </div>

    <!-- Charts -->
    <div class="chart-card">
      <h3>ยอดเดิมพัน</h3>
      <div class="chart-wrapper">
        <Line v-if="!loading" :data="betChartData" :options="lineOptions" />
        <div v-else class="chart-loading">Loading…</div>
      </div>
    </div>

    <div class="chart-card">
      <h3>กำไร / ขาดทุน</h3>
      <div class="chart-wrapper">
        <Line v-if="!loading" :data="winlossChartData" :options="lineOptions" />
        <div v-else class="chart-loading">Loading…</div>
      </div>
    </div>

    <div class="chart-card">
      <h3>จำนวนลูกค้า</h3>
      <div class="chart-wrapper">
        <Line v-if="!loading" :data="playerChartData" :options="lineOptions" />
        <div v-else class="chart-loading">Loading…</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line } from "vue-chartjs";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  ChartDataLabels
);

const dashboardRef = ref(null);
const loading = ref(false);

/* Period */
const ranges = [
  { label: "1M", value: "1M" },
  { label: "3M", value: "3M" },
  { label: "6M", value: "6M" },
  { label: "1Y", value: "1Y" },
];

const selectedRange = ref("1Y");

/* Data */
const dashboardData = ref({
  labels: [],
  bet: [],
  winloss: [],
  players: [],
});

/* Fetch API */
const fetchDashboardData = async (period) => {
  loading.value = true;
  try {
    const res = await fetch(
      `https://dashboard-api.linkstudio.io/api/v1/game/control/dashboard/weekly-chart?period=${period}`
    );
    const json = await res.json();
    if (!json.success) return;

    dashboardData.value = {
      labels: json.data.map((i) => i.week),
      bet: json.data.map((i) => i.bet_amount_avg),
      winloss: json.data.map((i) => i.win_loss_avg),
      players: json.data.map((i) => i.player_online_avg),
    };
  } catch (e) {
    console.error("Dashboard API error:", e);
  } finally {
    loading.value = false;
  }
};

/* Dataset helper */
const line = (label, data, color) => ({
  label,
  data,
  borderColor: color,
  backgroundColor: color.replace("1)", "0.25)"),
  tension: 0.35,
  pointRadius: 3,
  pointHoverRadius: 5,
});

/* Charts Data */
const betChartData = computed(() => ({
  labels: dashboardData.value.labels,
  datasets: [
    line("ยอดเดิมพันเฉลี่ยต่อสัปดาห์", dashboardData.value.bet, "rgba(59,130,246,1)"),
  ],
}));

const winlossChartData = computed(() => ({
  labels: dashboardData.value.labels,
  datasets: [
    line("กำไร / ขาดทุนเฉลี่ยต่อสัปดาห์", dashboardData.value.winloss, "rgba(244,63,94,1)"),
  ],
}));

const playerChartData = computed(() => ({
  labels: dashboardData.value.labels,
  datasets: [
    line("จำนวนลูกค้าเฉลี่ยต่อสัปดาห์", dashboardData.value.players, "rgba(34,197,94,1)"),
  ],
}));

/* Options */
const commonScales = {
  x: {
    ticks: {
      color: "#9aa4d6",
      minRotation: 25, // 🔑 เอียงขั้นต่ำ
      maxRotation: 25, // 🔑 เอียงคงที่
      autoSkip: true,  // ป้องกัน label แน่นเกิน
      padding: 6,
    },
    grid: { color: "rgba(255,255,255,0.06)" },
  },
  y: {
    ticks: { color: "#9aa4d6" },
    grid: { color: "rgba(255,255,255,0.06)" },
  },
};

const dataLabels = {
  datalabels: {
    display: "auto", // ไม่ให้ซ้อน
    clamp: true,
    anchor: "end",
    align: "top",
    offset: 4,
    color: "#e5e7ff",
    formatter: (v) => v.toLocaleString(),
    font: (ctx) => ({
      size: ctx.chart.width < 480 ? 9 : 10,
      weight: "600",
    }),
  },
};

const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 600 },
  plugins: {
    legend: { labels: { color: "#e5e7ff" } },
    ...dataLabels,
  },
  scales: commonScales,
};

/* Actions */
const changeRange = async (range) => {
  if (loading.value) return;
  selectedRange.value = range;
  await fetchDashboardData(range);
};

/* Mounted */
onMounted(async () => {
  await fetchDashboardData(selectedRange.value);
  await nextTick();
  dashboardRef.value?.setAttribute("data-ready", "true");
});
</script>

<style scoped>
.dashboard {
  background: rgb(5, 16, 57);
  min-height: 100vh;
  padding: 14px;
  color: #e5e7ff;
}

.header {
  margin-bottom: 12px;
}

.range-selector {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}

.range-selector button {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #1e2a6b;
  background: #0b1b5a;
  color: #c7d2fe;
  cursor: pointer;
}

.range-selector button.active {
  background: #3b82f6;
  color: #fff;
}

.range-selector button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Charts */
.chart-card {
  background: #0b1b5a;
  border: 1px solid #1e2a6b;
  border-radius: 10px;
  padding: 14px;
  height: 320px;
  margin-bottom: 16px;
}

.chart-card h3 {
  font-size: 14px;
  margin-bottom: 6px;
  color: #c7d2fe;
}

.chart-wrapper {
  position: relative;
  height: calc(100% - 28px);
}

/* Loading overlay */
.chart-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #c7d2fe;
  background: rgba(11, 27, 90, 0.6);
  border-radius: 8px;
}
</style>